import { EnumLanguages } from './../framework/sysenums.enum';
import { HttpErrorResponse, HttpEvent, HttpEventType, HttpHandler, HttpInterceptor, HttpProgressEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { BaseResponse } from '@base/entities/BaseEntities';
import { NavigatorService } from '../framework/navigator.service';
import { RC } from '../framework/resources.service';
import { EnumRequestProgressTracking } from '../framework/sysenums.enum';
import { SysGlobals } from '../framework/sysglobals';
import { Utils } from '../helpers/Utils';
import { SessionService } from '../framework/session.service';
import { LocalStorageService } from '../framework/local-storage.service';
import { ClientFingerprintService } from '../framework/client-fingerprint.service';
import { UiService } from '../framework/ui.service';

@Injectable()
export class APIGatewayInterceptor implements HttpInterceptor {
    private dateRegex = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*)?)(Z?)$/;
    private IsIphone: boolean;
    constructor(private session: SessionService,
        private deviceDetector: DeviceDetectorService,
        private localStorageService: LocalStorageService,
        private Navigator: NavigatorService,
        private ui: UiService,
        private clientFingerprintService: ClientFingerprintService) {
        if (this.deviceDetector.browser == "Safari" || this.deviceDetector.device == "ipad" || this.deviceDetector.device == "iphone") this.IsIphone = true;
    }

    //Formats value for sending to the server, without time zone
    private ConvertToServerDate(value: Date): string {
        var D = new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate(), value.getHours(), value.getMinutes(), value.getSeconds()));
        var S = D.toISOString();
        if (S.endsWith("Z")) S = S.substring(0, S.length - 1);
        return S;
    }

    //Append headers for communication
    private AddHeaders(request: HttpRequest<any>): HttpRequest<any> {

        // Set language
        var lang;
        try {
            lang = this.localStorageService.GetItem('Language');
        } catch (e) { }
        if (!lang) { lang = EnumLanguages.Hebrew; }
        request = request.clone({ headers: request.headers.set('m4u-language', lang) });

        if (!this.session.IsLoggedIn) return request;
        
        if (request.method == 'POST' && request.detectContentTypeHeader() == null 
            && (!request.params.has('autoContentType') || !request.params.get('autoContentType'))) { 

            request = request.clone({ headers: request.headers.set('Content-Type', 'application/octet-stream') }); 
        }

        const clientFingerprint = this.clientFingerprintService.GetClientFingerprint();
        request = request.clone({ headers: request.headers.set('m4u-client-fingerprint', clientFingerprint) });

        return request.clone({ headers: request.headers.set('m4u-session', this.session.SessionID as any) });
    }

    //Intercept the request pipeline
    public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        //Append headers
        const httpRequest: HttpRequest<any> = this.AddHeaders(request);

        //Turn on progress indicator
        if (this.GetTrackProgressHeader(httpRequest) != undefined) {
            SysGlobals.IsProgressTracking = true;
            SysGlobals.ProgressPercent = 0;
        }

        //Format dates on send
        this.FormatFields(httpRequest.body, 1);

        //Pipe request for additional handling
        return next.handle(httpRequest).pipe(
            tap((event: HttpEvent<any>) => this.HandleEvent(httpRequest, event)),
            catchError((httpResponse: any) => this.HandleError(httpRequest, httpResponse)));
    }

    //Return response
    private GetErrorResponse(sError: string, responseCode: number): BaseResponse {
        var response = new BaseResponse;
        response.IsSuccess = false;
        response.ResponseCode = responseCode;
        response.ResponseMessage = RC(sError);
        return response;
    }

    //If error has occured, replace the response with an error
    private HandleError(httpRequest: HttpRequest<any>, httpResponse: HttpErrorResponse) {
        //Handle according to error code
        var response: BaseResponse;
        switch (httpResponse.status) {
            case (0):
                //Means there is a communication problem. Did not get any response - write to console
                {
                    response = this.GetErrorResponse("errorNetwork", 999);
                    console.error(`Server communication problem - check your network - ${httpRequest.url}`);
                    console.error(httpResponse);
                    break;
                }


            case (401):
                //Means user is invalid. Server will return the error as the body
                //Redirect to login page
                {
                    response = httpResponse.error;
                    // this.session.ClearSessionData();
                    this.session.EndSession(() => { this.Navigator.Navigate(['/system/sessionexpired']); });
                    break;
                }


            case (402):
                //Means we have license issues. Server will return the error as the body
                {
                    response = httpResponse.error;
                    break;
                }

            case (403):
                //Means that session has expired - Re-direct
                {
                    response = this.GetErrorResponse("errorSessionExpired", httpResponse.status);
                    // this.session.ClearSessionData();
                    this.session.EndSession(() => { this.Navigator.Navigate(['/system/sessionexpired']); }, true)
                    break;
                }


            case (412):
                //Means user is not authorized for this action - return to the caller as is
                {
                    response = this.GetErrorResponse("errorUnauthorized", httpResponse.status);
                    break;
                }

            case (418):
                //Special case of a handled exception returned by the server. 
                //Server will return the error as the body
                //The server has logged the exception anyway. Convert the received data
                {
                    //Substitute data from the returned data
                    response = httpResponse.error;
                    if (response == null) response = new BaseResponse;
                    response.ResponseCode = httpResponse.status;
                    response.ResponseMessage = RC("errorUnexpected", Utils.NZ(response.ResponseMessage));


                    //Write to console
                    console.error(`Server exception received: \n ${Utils.NZ(response.ResponseMessage)} \n ${Utils.NZ(response.ResponseExtendedMessage)}`);
                    break;
                }
            case (419):
                //Means that MID is missing or wrong
                response = httpResponse.error;
                this.Navigator.Navigate(['/system/midstore']);
                break;

            case (423):
                //Password expired - logout, and redirect
                // this.session.LogoutAndResetSession();
                response = this.GetErrorResponse("errorPasswordExpired", httpResponse.status);
                const userName = this.session.UserInfos && this.session.UserInfos.length ? this.session.UserInfos[0]?.Name : undefined;
                this.session.EndSession(() => { this.Navigator.Navigate(['/system/passwordchangeexpired'], { UserName: userName }); });
                break;

            case (430):                    
                    // Session is frozen
                    response = this.GetErrorResponse("errorSessionIsFrozen", httpResponse.status);
                    this.ui.StopMessage(response.ResponseMessage);
                    console.error(response.ResponseMessage);
                    break;
    
            default:
                //Unexpected errors
                {
                    //Substitute data from the status code
                    response = new BaseResponse;
                    response.IsSuccess = false;
                    response.ResponseCode = httpResponse.status;
                    response.ResponseMessage = httpResponse.statusText;

                    //Write to console
                    let stack = new Error(httpResponse.message).stack;
                    let errMsg = (httpResponse.message) ? httpResponse.message : httpResponse.status ? `${httpResponse.status} - ${httpResponse.statusText}` : 'Server error';
                    console.error(errMsg);
                    if (stack) console.error(stack);

                    break;
                }
        }

        //Turn off progress indicator
        if (this.GetTrackProgressHeader(httpRequest) != undefined) SysGlobals.IsProgressTracking = false;

        //Return as observable
        return new Observable<HttpEvent<any>>((observer) => {
            var httpResponse = new HttpResponse({ body: response });
            observer.next(httpResponse);
            observer.complete();
        })

    }

    //Tap into HTTP events
    private HandleEvent(httpRequest: HttpRequest<any>, httpEvent: HttpEvent<any>) {
        //Handle request finish
        if (httpEvent instanceof HttpResponse) {
            //Format response data
            this.FormatFields(httpEvent.body, 2);

            //Turn off progress indicator
            if (this.GetTrackProgressHeader(httpRequest) != undefined) SysGlobals.IsProgressTracking = false;
        }

        //Progress tracking
        if (httpEvent.type == HttpEventType.DownloadProgress || httpEvent.type == HttpEventType.UploadProgress) {
            var track: EnumRequestProgressTracking = this.GetTrackProgressHeader(httpRequest);
            if (track != undefined) this.HandleTrackProgressEvent(httpEvent as HttpProgressEvent, track);
        }

        if (!httpRequest.context.get(SysGlobals.IgnoreSessionRefreshRequestToken)) {
            this.session.RefreshExpiredSessionTimeout();
        }
    }

    //Return the track progress header indicator
    private GetTrackProgressHeader(request: HttpRequest<any>): EnumRequestProgressTracking {
        var header = request.headers.get("m4u-trackprogress");
        return header ? header[0] as EnumRequestProgressTracking : undefined;
    }

    //Handle progress events
    private HandleTrackProgressEvent(event: HttpProgressEvent, TrackProgress: EnumRequestProgressTracking) {

        //If tracking should be disabled (shouldnt get here anyway)
        if (TrackProgress == EnumRequestProgressTracking.Send && event.type == HttpEventType.DownloadProgress) return;
        if (TrackProgress == EnumRequestProgressTracking.Receive && event.type == HttpEventType.UploadProgress) return;

        //Set the percent, depending on what we're tracking
        var fPercent: number = event.loaded / event.total * 100;
        if (TrackProgress == EnumRequestProgressTracking.Both) {
            if (event.type == HttpEventType.UploadProgress) fPercent = fPercent * 0.5;
            if (event.type == HttpEventType.DownloadProgress) fPercent = 50 + fPercent * 0.5;
        }

        //Set progress
        SysGlobals.ProgressPercent = fPercent;

    }

    //
    private FormatFields(object: Object, iDirection: number) {


        //Skip nulls
        if (!object || !(object instanceof Object)) return;


        //Recurse arrays
        if (object instanceof Array) {
            for (const item of object) {
                this.FormatFields(item, iDirection);
            }
        }


        for (const key of Object.keys(object)) {

            //Recurse arrays
            const value = object[key];
            if (value instanceof Array) {
                for (const item of value) {
                    this.FormatFields(item, iDirection);
                }
            }

            //Recurse objects
            if (value instanceof Object) this.FormatFields(value, iDirection);

            //Format dates on sending to local date
            if (iDirection == 1) {
                if (value instanceof Date) object[key] = this.ConvertToServerDate(value);
            }

            //Format on receiving
            if (iDirection == 2) {

                //Format dates on receiving as Date object
                //Notice the special fix for iPHONE - otherwise dates are shifted by the timezone
                if (typeof value === 'string' && this.dateRegex.test(value)) {
                    if (this.IsIphone && value != null) {
                        object[key] = new Date(
                            (+value.substring(0, 4)),
                            (+value.substring(5, 7) - 1),
                            (+value.substring(8, 10)),
                            (+value.substring(11, 13)),
                            (+value.substring(14, 16)),
                            (+value.substring(17, 19)));
                    }
                    else {
                        object[key] = new Date(value);
                    }
                }

                //Replace null values returned by the server with undefined, to maintain consistancy
                if (value === null) object[key] = undefined;
            }
        }
    }
}
